// src/pages/DashboardFinance.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    useTheme,
    Switch,
    FormControlLabel,
} from "@mui/material";
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    Legend as RechartsLegend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Label,
} from "recharts";
import axios from "axios";

import StatBox from "../../component-files/StatBox";
import Header from "../../component-files/Header";
import { tokens } from "../../theme";
import swanLogo from "../../assets/swanailogo.png";
import { useAuth } from "../../context/AuthContext";

const API_ENDPOINTS = {
    expenses: "https://figazuresql.swancreate.com/api/swa/vw_aichat_ExpenseTotalsMonthly",
    incomes: "https://figazuresql.swancreate.com/api/swa/vw_aichat_IncomeTotalsMonthly",
};

export const INCOME_COLORS = ["#2E8B57", "#3CB371", "#20B2AA", "#00B4D8", "#1E90FF", "#4BA3C3"];
export const EXPENSE_COLORS = ["#D62828", "#FF4C4C", "#FF8C00", "#FFB000", "#FFD54F", "#FFC300"];
const DARK_RED = "#8B0000";
const DARK_GREEN = "#006400";

const getCurrentSYStart = (d = new Date()) => (d.getMonth() >= 6 ? d.getFullYear() : d.getFullYear() - 1);
const syLabel = (y) => `${y}-${y + 1}`;
const inSchoolYear = (yr, mo, startYear) => {
    const dt = new Date(yr, mo - 1, 1);
    return dt >= new Date(startYear, 6, 1) && dt < new Date(startYear + 1, 6, 1);
};
const formatCurrency = (v) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(v);
const pct = (n, d) => (d > 0 ? Math.round((n / d) * 100) : 0);

// ---- YearMonth parsing with ALWAYS-KEEP fallback to current YYYY-MM ----
const currentYM = () => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() + 1 };
};
const parseYMOrNow = (ym) => {
    if (typeof ym === "string") {
        const parts = ym.split("-");
        if (parts.length === 2) {
            const yr = Number(parts[0]);
            const mo = Number(parts[1]);
            if (Number.isFinite(yr) && Number.isFinite(mo) && mo >= 1 && mo <= 12) {
                return { year: yr, month: mo };
            }
        }
    }
    // Fallback: use current YYYY-MM, never drop the row
    return currentYM();
};

export default function DashboardFinance() {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const textColor = theme.palette.text.primary;
    const bgPaper = theme.palette.background.paper;
    const bgDefault = theme.palette.background.default;
    const gridColor = theme.palette.divider;

    const auth = useAuth() || {};
    const allocations = auth.allocations || [];

    // site list from allocations, unique + alpha
    const siteOptions = useMemo(() => {
        const names = Array.from(
            new Set((allocations || []).map((a) => a?.SchoolName).filter(Boolean))
        ).sort((a, b) => a.localeCompare(b));
        return ["All", ...names];
    }, [allocations]);

    // filters
    const [selectedSite, setSelectedSite] = useState("All");
    const [syStart, setSyStart] = useState(getCurrentSYStart());
    const [monthFilter, setMonth] = useState("All");
    const [comparePrev, setCompare] = useState(false);

    // aggregated state
    const [catIncTotals, setCatIncTotals] = useState({});
    const [catExpTotals, setCatExpTotals] = useState({});
    const [subIncTotals, setSubIncTotals] = useState({});
    const [foodExpTotals, setFoodExpTotals] = useState({});
    const [laborExpTotals, setLaborExpTotals] = useState({});
    const [otherExpTotals, setOtherExpTotals] = useState({});
    const [chartData, setChartData] = useState([]);
    const [profitLoss, setProfitLoss] = useState(0);
    const [totalInc, setTotalInc] = useState(0);

    useEffect(() => {
        (async () => {
            try {
                const [eRes, iRes] = await Promise.all([
                    axios.get(API_ENDPOINTS.expenses),
                    axios.get(API_ENDPOINTS.incomes),
                ]);

                const expRows = Array.isArray(eRes.data) ? eRes.data : [];
                const incRows = Array.isArray(iRes.data) ? iRes.data : [];

                // Map every row; YearMonth falls back to current when missing/invalid
                const rawExp = expRows
                    .map((r) => {
                        const ym = parseYMOrNow(r?.YearMonth);
                        return {
                            schoolName: r?.SchoolName ?? "",
                            ...ym,
                            category: r?.kpi_categoryname ?? r?.CategoryName ?? "Other",
                            sub: r?.SubcategoryName ?? "Uncategorized",
                            value: Number(r?.TotalExpense ?? 0),
                        };
                    })
                    .filter((r) => selectedSite === "All" || r.schoolName === selectedSite);

                const rawInc = incRows
                    .map((r) => {
                        const ym = parseYMOrNow(r?.YearMonth);
                        return {
                            schoolName: r?.SchoolName ?? "",
                            ...ym,
                            sub: r?.SubcategoryName ?? "Uncategorized",
                            value: Number(r?.TotalIncome ?? 0),
                        };
                    })
                    .filter((r) => selectedSite === "All" || r.schoolName === selectedSite);

                // month filter
                const mf = monthFilter === "All" ? () => true : (_, m) => m === +monthFilter;

                // current SY subset
                const curExp = rawExp.filter((e) => inSchoolYear(e.year, e.month, syStart) && mf(e, e.month));
                const curInc = rawInc.filter((i) => inSchoolYear(i.year, i.month, syStart) && mf(i, i.month));

                const aggr = (arr, keyFn) =>
                    arr.reduce((acc, r) => {
                        const k = keyFn(r);
                        acc[k] = (acc[k] || 0) + r.value;
                        return acc;
                    }, {});

                // stat boxes
                setCatExpTotals(aggr(curExp, (r) => r.category));
                setCatIncTotals(aggr(curInc, (r) => r.sub));

                // pie data
                setSubIncTotals(aggr(curInc, (r) => r.sub));
                setFoodExpTotals(aggr(curExp.filter((r) => r.category === "Food"), (r) => r.sub));
                setLaborExpTotals(aggr(curExp.filter((r) => r.category === "Labor"), (r) => r.sub));
                setOtherExpTotals(
                    aggr(curExp.filter((r) => !["Food", "Labor"].includes(r.category)), (r) => r.sub)
                );

                // profit/loss
                const totI = curInc.reduce((s, r) => s + r.value, 0);
                const totE = curExp.reduce((s, r) => s + r.value, 0);
                setProfitLoss(totI - totE);
                setTotalInc(totI);

                // build area chart
                const monthsArr = [7, 8, 9, 10, 11, 12, 1, 2, 3, 4, 5, 6];

                let prevMap = {};
                if (comparePrev) {
                    const pStart = syStart - 1;
                    const pExp = rawExp.filter((e) => inSchoolYear(e.year, e.month, pStart) && mf(e, e.month));
                    const pInc = rawInc.filter((i) => inSchoolYear(i.year, i.month, pStart) && mf(i, i.month));
                    monthsArr.forEach((m) => {
                        prevMap[m] = {
                            ExpensesPrev: pExp.filter((e) => e.month === m).reduce((s, e) => s + e.value, 0),
                            IncomePrev: pInc.filter((i) => i.month === m).reduce((s, i2) => s + i2.value, 0),
                        };
                    });
                }

                setChartData(
                    monthsArr.map((m) => ({
                        month: new Date(0, m - 1).toLocaleString("en-US", { month: "short" }),
                        Expenses: curExp.filter((e) => e.month === m).reduce((s, e) => s + e.value, 0),
                        Income: curInc.filter((i) => i.month === m).reduce((s, i2) => s + i2.value, 0),
                        ...(comparePrev ? prevMap[m] : {}),
                    }))
                );
            } catch (err) {
                console.error(err);
            }
        })();
    }, [syStart, monthFilter, comparePrev, selectedSite]);

    const PieWithCenter = ({ data, colors, centerText, glowColor }) => {
        const entries = Object.entries(data).map(([name, value]) => ({ name, value }));
        return (
            <Box sx={{ p: 2, borderRadius: 2, boxShadow: `0 0 20px ${glowColor}`, bgcolor: bgPaper }}>
                <ResponsiveContainer width="100%" height={350}>
                    <PieChart>
                        <Pie
                            data={entries}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius="50%"
                            outerRadius="80%"
                            labelLine
                            label={({ name, value }) => `${name}: ${formatCurrency(value)}`}
                        >
                            {entries.map((_, i) => (
                                <Cell key={i} fill={colors[i % colors.length]} />
                            ))}
                            {centerText != null && (
                                <Label value={centerText} position="center" fill="#FFF" fontSize="2rem" fontWeight="bold" />
                            )}
                        </Pie>
                        <RechartsTooltip formatter={formatCurrency} />
                        <RechartsLegend layout="vertical" align="right" verticalAlign="middle" wrapperStyle={{ color: textColor }} />
                    </PieChart>
                </ResponsiveContainer>
            </Box>
        );
    };

    const foodPct = pct(Object.values(foodExpTotals).reduce((s, v) => s + v, 0), totalInc);
    const laborPct = pct(Object.values(laborExpTotals).reduce((s, v) => s + v, 0), totalInc);
    const otherPct = pct(Object.values(otherExpTotals).reduce((s, v) => s + v, 0), totalInc);

    return (
        <Box m={2} p={3} sx={{ bgcolor: bgDefault, color: textColor, position: "relative", overflow: "hidden" }}>
            <Box
                component="img"
                src={swanLogo}
                alt="Swan AI"
                sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%,-50%)",
                    width: 600,
                    opacity: 0.05,
                    pointerEvents: "none",
                    zIndex: 0,
                }}
            />

            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                    <Header title="PROGRAM PROFIT / LOSS" subtitle="Overview of financial metrics" />
                    <Box mt={1} display="flex" alignItems="center" gap={2}>
                        {/* site dropdown */}
                        <FormControl sx={{ minWidth: 200 }}>
                            <InputLabel sx={{ color: textColor }}>Site</InputLabel>
                            <Select
                                value={selectedSite}
                                onChange={(e) => setSelectedSite(e.target.value)}
                                label="Site"
                                sx={{ color: textColor, ".MuiOutlinedInput-notchedOutline": { borderColor: textColor } }}
                            >
                                {siteOptions.map((name) => (
                                    <MenuItem key={name} value={name}>
                                        {name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl sx={{ minWidth: 160 }}>
                            <InputLabel sx={{ color: textColor }}>Month</InputLabel>
                            <Select
                                value={monthFilter}
                                onChange={(e) => setMonth(e.target.value)}
                                label="Month"
                                sx={{ color: textColor, ".MuiOutlinedInput-notchedOutline": { borderColor: textColor } }}
                            >
                                <MenuItem value="All">All</MenuItem>
                                {Array.from({ length: 12 }, (_, i) => (
                                    <MenuItem key={i + 1} value={i + 1}>
                                        {new Date(0, i).toLocaleString("en-US", { month: "long" })}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl sx={{ minWidth: 180 }}>
                            <InputLabel sx={{ color: textColor }}>School Year</InputLabel>
                            <Select
                                value={syStart}
                                onChange={(e) => setSyStart(+e.target.value)}
                                label="School Year"
                                sx={{ color: textColor, ".MuiOutlinedInput-notchedOutline": { borderColor: textColor } }}
                            >
                                {Array.from({ length: 5 }, (_, i) => {
                                    const y = getCurrentSYStart() - i;
                                    return (
                                        <MenuItem key={y} value={y}>
                                            {syLabel(y)}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>

                        <FormControlLabel
                            control={<Switch checked={comparePrev} onChange={(e) => setCompare(e.target.checked)} color="secondary" />}
                            label="Compare Previous SY"
                            sx={{ color: textColor }}
                        />
                    </Box>
                </Box>

                <Box
                    sx={{
                        px: 6,
                        py: 4,
                        borderRadius: 2,
                        boxShadow: `0 0 30px ${profitLoss >= 0 ? theme.palette.success.main : theme.palette.error.main}`,
                        textAlign: "center",
                        bgcolor: bgPaper,
                    }}
                >
                    <Typography
                        variant="h1"
                        sx={{
                            color: profitLoss >= 0 ? theme.palette.success.main : theme.palette.error.main,
                            fontWeight: "bold",
                            lineHeight: 1,
                        }}
                    >
                        {formatCurrency(profitLoss)}
                    </Typography>
                    <Typography variant="h6">Total Profit / Loss</Typography>
                </Box>
            </Box>

            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4} my={5}>
                <Box>
                    <Typography variant="h6" mb={2}>
                        Income by Sub-category
                    </Typography>
                    <PieWithCenter data={subIncTotals} colors={INCOME_COLORS} centerText={null} glowColor={theme.palette.success.main} />
                </Box>
                <Box>
                    <Typography variant="h6" mb={2}>
                        Food Expense by Sub-category
                    </Typography>
                    <PieWithCenter data={foodExpTotals} colors={EXPENSE_COLORS} centerText={`${foodPct}%`} glowColor={theme.palette.error.main} />
                </Box>
                <Box>
                    <Typography variant="h6" mb={2}>
                        Labor Expense by Sub-category
                    </Typography>
                    <PieWithCenter data={laborExpTotals} colors={EXPENSE_COLORS} centerText={`${laborPct}%`} glowColor={theme.palette.error.main} />
                </Box>
                <Box>
                    <Typography variant="h6" mb={2}>
                        Other Expense by Sub-category
                    </Typography>
                    <PieWithCenter data={otherExpTotals} colors={EXPENSE_COLORS} centerText={`${otherPct}%`} glowColor={theme.palette.error.main} />
                </Box>
            </Box>

            <Box my={5} p={3} borderRadius={2} sx={{ bgcolor: bgPaper }}>
                <Typography variant="h6" fontWeight={600} mb={3}>
                    Income vs Expenses — {syLabel(syStart)}
                    {comparePrev && ` vs ${syLabel(syStart - 1)}`}
                </Typography>
                <ResponsiveContainer width="100%" height={350}>
                    <AreaChart data={chartData}>
                        <defs>
                            <linearGradient id="expGrad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={theme.palette.error.main} stopOpacity={0.55} />
                                <stop offset="95%" stopColor={theme.palette.error.main} stopOpacity={0} />
                            </linearGradient>
                            <linearGradient id="incGrad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={theme.palette.success.main} stopOpacity={0.55} />
                                <stop offset="95%" stopColor={theme.palette.success.main} stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                        <XAxis dataKey="month" stroke={textColor} />
                        <YAxis stroke={textColor} />
                        <RechartsTooltip formatter={formatCurrency} />
                        <RechartsLegend />
                        <Area
                            type="monotone"
                            dataKey="Expenses"
                            stroke={theme.palette.error.main}
                            fill="url(#expGrad)"
                            strokeWidth={3}
                            dot={{ r: 4, stroke: theme.palette.error.main, strokeWidth: 2 }}
                        />
                        <Area
                            type="monotone"
                            dataKey="Income"
                            stroke={theme.palette.success.main}
                            fill="url(#incGrad)"
                            strokeWidth={3}
                            dot={{ r: 4, stroke: theme.palette.success.main, strokeWidth: 2 }}
                        />
                        {comparePrev && (
                            <>
                                <Area
                                    type="monotone"
                                    dataKey="ExpensesPrev"
                                    stroke={DARK_RED}
                                    fill="none"
                                    strokeWidth={2}
                                    strokeDasharray="4 4"
                                    dot={false}
                                    name="Expenses (prev)"
                                />
                                <Area
                                    type="monotone"
                                    dataKey="IncomePrev"
                                    stroke={DARK_GREEN}
                                    fill="none"
                                    strokeWidth={2}
                                    strokeDasharray="4 4"
                                    dot={false}
                                    name="Income (prev)"
                                />
                            </>
                        )}
                    </AreaChart>
                </ResponsiveContainer>
            </Box>

            <Box display="grid" gridTemplateColumns="repeat(auto-fit,minmax(240px,1fr))" gap={3}>
                {Object.entries(catIncTotals).map(([cat, total], i) => (
                    <StatBox
                        key={`inc-${cat}`}
                        title={formatCurrency(total)}
                        subtitle={`Income: ${cat}`}
                        borderColor={INCOME_COLORS[i % INCOME_COLORS.length]}
                    />
                ))}
                {Object.entries(catExpTotals).map(([cat, total], i) => (
                    <StatBox
                        key={`exp-${cat}`}
                        title={formatCurrency(total)}
                        subtitle={`Expense: ${cat}`}
                        borderColor={EXPENSE_COLORS[i % EXPENSE_COLORS.length]}
                    />
                ))}
            </Box>
        </Box>
    );
}
